import { getRouteApi, Link } from "@tanstack/react-router";
import { ArrowLeft, Loader2 } from "lucide-react";
import { useMemo } from "react";
import { m } from "@/paraglide/messages";
import {
  BarChart,
  ChartCard,
  H2HPositionChart,
  HeatmapStrip,
  LineChart,
  StatCounter,
} from "../components/statistics";
import { LazyChartCard } from "../components/statistics/LazyChartCard";
import { useDriverProfile } from "../hooks/use-statistics";
import { DEFAULT_SEASON } from "../lib/constants";
import { TRACK_CLASSIFICATION } from "../lib/statistics/track-classification";
import { getTeamColor } from "../lib/team-colors";

const routeApi = getRouteApi("/f1-fantasy/statistics/drivers/$slug");

export function DriverProfile() {
  const { slug } = routeApi.useParams();
  const loaderData = routeApi.useLoaderData() as { season: number } | undefined;
  const { season: searchSeason, context: rawContext } = routeApi.useSearch();
  // Prefer URL param (always in sync with address bar) over loader-resolved value
  const season = searchSeason ?? loaderData?.season ?? DEFAULT_SEASON;
  const context = rawContext ?? "fantasy";

  const { data: profile, isLoading } = useDriverProfile(slug, season);
  const showLoading = isLoading && !profile;

  const teamColor = getTeamColor(profile?.constructorSlug ?? "");
  const totalChampPoints =
    profile?.races.reduce((s, r) => s + r.champPoints, 0) ?? 0;

  // Prepare chart data
  const chartData = useMemo(() => {
    if (!profile || !profile.races.length) return null;

    const races = profile.races;
    const labels = races.map((r) => r.raceName.replace(/ Grand Prix/i, ""));

    return {
      labels,
      // 1. Points Over Time
      points: races.map((r) => r.fantasyPoints),
      // 1b. Cumulative fantasy points
      cumulativeFantasyPoints: races.reduce((acc, r) => {
        const prev = acc.length > 0 ? acc[acc.length - 1] : 0;
        acc.push(prev + r.fantasyPoints);
        return acc;
      }, [] as number[]),
      // 2. Consistency Heatmap
      heatmapValues: races.map((r) => r.fantasyPoints),
      // 3. Qualifying vs Race Position
      qualiPositions: races.map((r) => r.qualifyingPosition),
      racePositions: races.map((r) => r.racePosition),
      // 4. Radar chart - Points Breakdown
      radarData: (() => {
        const qualiPtsProxy = races.filter(
          (r) => r.qualifyingPosition != null && r.qualifyingPosition <= 10,
        ).length;
        const overtakePts = races.reduce((s, r) => s + r.overtakes, 0);
        const posGainPts = races.reduce(
          (s, r) => s + Math.max(0, r.positionsGained),
          0,
        );
        const flPts = races.filter((r) => r.fastestLap).length;
        const dotdPts = races.filter((r) => r.driverOfTheDay).length;
        return { qualiPtsProxy, overtakePts, posGainPts, flPts, dotdPts };
      })(),
      // 5. Head-to-Head (fantasy)
      h2hData: (() => {
        if (!profile.teammateRaces.length) return null;
        const tmByRace = new Map(
          profile.teammateRaces.map((r) => [r.raceSlug, r]),
        );
        const driverPts: number[] = [];
        const teammatePts: number[] = [];
        const h2hLabels: string[] = [];

        for (const r of races) {
          const tm = tmByRace.get(r.raceSlug);
          if (!tm) continue;
          h2hLabels.push(r.raceName.replace(/ Grand Prix/i, ""));
          driverPts.push(r.fantasyPoints);
          teammatePts.push(tm.fantasyPoints);
        }
        return { h2hLabels, driverPts, teammatePts };
      })(),
      // 5b. Head-to-Head race positions
      h2hRaceData: (() => {
        if (!profile.teammateRaces.length) return null;
        const tmByRace = new Map(
          profile.teammateRaces.map((r) => [r.raceSlug, r]),
        );
        const driverPos: (number | null)[] = [];
        const teammatePos: (number | null)[] = [];
        const h2hLabels: string[] = [];

        for (const r of races) {
          if (r.racePosition == null) continue;
          const tm = tmByRace.get(r.raceSlug);
          if (!tm || tm.racePosition == null) continue;
          h2hLabels.push(r.raceName.replace(/ Grand Prix/i, ""));
          driverPos.push(r.racePosition);
          teammatePos.push(tm.racePosition);
        }
        return h2hLabels.length > 0
          ? { h2hLabels, driverPos, teammatePos }
          : null;
      })(),
      // 5c. Head-to-Head qualifying positions
      h2hQualiData: (() => {
        if (!profile.teammateRaces.length) return null;
        const tmByRace = new Map(
          profile.teammateRaces.map((r) => [r.raceSlug, r]),
        );
        const driverPos: (number | null)[] = [];
        const teammatePos: (number | null)[] = [];
        const h2hLabels: string[] = [];

        for (const r of races) {
          if (r.qualifyingPosition == null) continue;
          const tm = tmByRace.get(r.raceSlug);
          if (!tm || tm.qualifyingPosition == null) continue;
          h2hLabels.push(r.raceName.replace(/ Grand Prix/i, ""));
          driverPos.push(r.qualifyingPosition);
          teammatePos.push(tm.qualifyingPosition);
        }
        return h2hLabels.length > 0
          ? { h2hLabels, driverPos, teammatePos }
          : null;
      })(),
      // Race positions over time (for race context)
      racePositions2: races.map((r) => r.racePosition),
      // Positions gained per race
      positionsGainedPerRace: races.map((r) => r.positionsGained),
      // Championship points per race (actual from DB — includes sprint race pts)
      champPointsPerRace: races.map((r) => r.champPoints),
      // Cumulative championship points over the season
      cumulativeChampPoints: races.reduce((acc, r) => {
        const prev = acc.length > 0 ? acc[acc.length - 1] : 0;
        acc.push(prev + r.champPoints);
        return acc;
      }, [] as number[]),
      // Finishing position distribution
      positionDistribution: (() => {
        const wins = races.filter((r) => r.racePosition === 1).length;
        const podiumsExcl1 = races.filter(
          (r) =>
            r.racePosition != null &&
            r.racePosition >= 2 &&
            r.racePosition <= 3,
        ).length;
        const top10ExclPodium = races.filter(
          (r) =>
            r.racePosition != null &&
            r.racePosition >= 4 &&
            r.racePosition <= 10,
        ).length;
        const outOfPoints = races.filter(
          (r) => r.racePosition != null && r.racePosition >= 11 && !r.dnf,
        ).length;
        const dnfs = races.filter((r) => r.dnf).length;
        return { wins, podiumsExcl1, top10ExclPodium, outOfPoints, dnfs };
      })(),
      // Track-type average race position (for race context)
      trackRacePos: (() => {
        const byType: Record<string, number[]> = {
          street: [],
          high_speed: [],
          technical: [],
          mixed: [],
        };
        for (const r of races) {
          if (r.racePosition == null) continue;
          const trackType = TRACK_CLASSIFICATION[r.raceSlug] ?? "mixed";
          byType[trackType].push(r.racePosition);
        }
        return Object.entries(byType).map(([type, pos]) => ({
          type,
          avgPos:
            pos.length > 0
              ? Math.round((pos.reduce((a, b) => a + b, 0) / pos.length) * 10) /
                10
              : null,
          count: pos.length,
        }));
      })(),
      // Fantasy score distribution (for fantasy context)
      fantasyScoreDistribution: (() => {
        const buckets = [0, 0, 0, 0, 0]; // ≤0, 1–14, 15–29, 30–44, 45+
        for (const r of races) {
          if (r.fantasyPoints <= 0) buckets[0]++;
          else if (r.fantasyPoints <= 14) buckets[1]++;
          else if (r.fantasyPoints <= 29) buckets[2]++;
          else if (r.fantasyPoints <= 44) buckets[3]++;
          else buckets[4]++;
        }
        return buckets;
      })(),
      // Race heatmap values (inverted position: P1=20, P20=1, DNF=0)
      raceHeatmapValues: races.map((r) =>
        r.dnf || r.racePosition == null ? 0 : Math.max(0, 21 - r.racePosition),
      ),
      // Race performance breakdown (for race context)
      raceBreakdown: {
        fastestLaps: races.filter((r) => r.fastestLap).length,
        dotd: races.filter((r) => r.driverOfTheDay).length,
        totalOvertakes: races.reduce((s, r) => s + r.overtakes, 0),
        totalPosGained: races.reduce(
          (s, r) => s + Math.max(0, r.positionsGained),
          0,
        ),
      },
      // 6. Price History
      prices: races.map((r) => r.price),
      // 7. Track-Type Performance
      trackPerf: (() => {
        const byType: Record<string, number[]> = {
          street: [],
          high_speed: [],
          technical: [],
          mixed: [],
        };
        for (const r of races) {
          const trackType = TRACK_CLASSIFICATION[r.raceSlug] ?? "mixed";
          byType[trackType].push(r.fantasyPoints);
        }
        return Object.entries(byType).map(([type, pts]) => ({
          type,
          avgPts:
            pts.length > 0
              ? Math.round((pts.reduce((a, b) => a + b, 0) / pts.length) * 10) /
                10
              : 0,
          count: pts.length,
        }));
      })(),
      trackNames: (() => {
        const byType: Record<string, string[]> = {
          street: [],
          high_speed: [],
          technical: [],
          mixed: [],
        };
        for (const r of races) {
          const trackType = TRACK_CLASSIFICATION[r.raceSlug] ?? "mixed";
          byType[trackType].push(r.raceName.replace(/ Grand Prix/i, ""));
        }
        return byType;
      })(),
    };
  }, [profile]);

  if (showLoading) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center gap-4">
        <p className="text-muted-foreground">
          {m.f1_profile_driver_not_found()}
        </p>
        <Link to="/f1-fantasy/statistics" className="text-primary text-sm">
          {m.f1_profile_back_to_stats()}
        </Link>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="border-b border-border bg-card/50 backdrop-blur-sm sticky top-16 z-40">
        <div className="container mx-auto px-4 py-3">
          <div className="flex items-center gap-3">
            <Link
              to="/f1-fantasy/statistics"
              search={{
                season,
                tab: context === "race" ? "race_results" : "fantasy_stats",
              }}
              className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors"
              data-testid="back-to-statistics"
            >
              <ArrowLeft className="h-4 w-4" />
              <span className="text-sm">{m.f1_stats_title()}</span>
            </Link>
            <div className="w-px h-5 bg-border" />
            <span className="font-medium text-sm truncate">
              {profile.driverFullName}
            </span>
            <div className="ml-auto flex items-center gap-0 rounded-lg border border-border bg-muted/30 p-0.5">
              <Link
                to="/f1-fantasy/statistics/drivers/$slug"
                params={{ slug }}
                search={{ season, context: "race" }}
                className={`px-3 py-1.5 rounded-md text-xs font-semibold transition-all ${context === "race" ? "bg-primary text-primary-foreground shadow-sm" : "text-muted-foreground hover:text-foreground"}`}
              >
                {m.f1_profile_context_race()}
              </Link>
              <Link
                to="/f1-fantasy/statistics/drivers/$slug"
                params={{ slug }}
                search={{ season, context: "fantasy" }}
                className={`px-3 py-1.5 rounded-md text-xs font-semibold transition-all ${context === "fantasy" ? "bg-primary text-primary-foreground shadow-sm" : "text-muted-foreground hover:text-foreground"}`}
              >
                {m.f1_profile_context_fantasy()}
              </Link>
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8">
        {/* Hero Section */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-4">
            <div
              className="w-1.5 h-10 rounded-full"
              style={{ backgroundColor: teamColor }}
            />
            <div>
              <h1 className="font-display text-2xl sm:text-3xl font-bold text-foreground">
                {profile.driverFullName}
              </h1>
              <p className="text-sm text-muted-foreground">
                {profile.constructorName} — {season}
              </p>
            </div>
          </div>

          {/* Headline Stats — same positions in both tabs */}
          <div className="grid grid-cols-3 sm:grid-cols-5 gap-4 sm:gap-6">
            {/* 1. Total points (championship or fantasy) */}
            <StatCounter
              value={
                context === "race" ? totalChampPoints : profile.totalPoints
              }
              label={
                context === "race"
                  ? m.f1_profile_stat_championship_pts()
                  : m.f1_profile_stat_total_points()
              }
              decimals={context === "race" ? 0 : 1}
            />
            {/* 2-4. Shared */}
            <StatCounter
              value={profile.wins}
              label={m.f1_profile_stat_wins()}
            />
            <StatCounter
              value={profile.podiums}
              label={m.f1_profile_stat_podiums()}
            />
            <StatCounter
              value={profile.totalRaces}
              label={m.f1_profile_stat_races()}
            />
            {/* 5. Tab-specific */}
            {context === "race" ? (
              <StatCounter
                value={
                  profile.races.filter(
                    (r) => r.racePosition != null && r.racePosition <= 10,
                  ).length
                }
                label={m.f1_profile_stat_points_finishes()}
              />
            ) : (
              <StatCounter
                value={profile.avgFantasyPoints}
                label={m.f1_profile_stat_avg_fantasy()}
                decimals={1}
              />
            )}
          </div>
        </div>

        {/* Charts Grid */}
        {chartData && context === "race" ? (
          <div className="grid gap-4 lg:grid-cols-2">
            {/* 1. Championship Points Per Race */}
            <ChartCard
              title={m.f1_profile_chart_champ_pts_per_race()}
              tooltip={m.f1_profile_tooltip_champ_pts()}
            >
              <BarChart
                categories={chartData.labels}
                series={[
                  {
                    name: "Points",
                    data: chartData.champPointsPerRace,
                    color: teamColor,
                  },
                ]}
              />
            </ChartCard>

            {/* 2. Cumulative Championship Points */}
            <ChartCard
              title={m.f1_profile_chart_cumulative_pts()}
              tooltip={m.f1_profile_tooltip_cumulative_pts()}
            >
              <LineChart
                categories={chartData.labels}
                series={[
                  {
                    name: "Total Points",
                    data: chartData.cumulativeChampPoints,
                    color: teamColor,
                    areaStyle: true,
                  },
                ]}
                showLegend={false}
              />
            </ChartCard>

            {/* 3. Qualifying vs Race Position */}
            <LazyChartCard>
              <ChartCard
                title={m.f1_profile_chart_quali_vs_race()}
                tooltip={m.f1_profile_tooltip_quali_vs_race()}
              >
                <LineChart
                  categories={chartData.labels}
                  series={[
                    {
                      name: m.f1_profile_series_qualifying(),
                      data: chartData.qualiPositions,
                      color: "#8b5cf6",
                    },
                    {
                      name: m.f1_profile_series_race(),
                      data: chartData.racePositions,
                      color: teamColor,
                    },
                  ]}
                  invertYAxis
                  yAxisName={m.f1_profile_yaxis_position()}
                />
              </ChartCard>
            </LazyChartCard>

            {/* 4. Positions Gained Per Race */}
            <LazyChartCard>
              <ChartCard
                title={m.f1_profile_chart_pos_gained()}
                tooltip={m.f1_profile_tooltip_pos_gained()}
              >
                <BarChart
                  categories={chartData.labels}
                  series={[
                    {
                      name: "Positions",
                      data: chartData.positionsGainedPerRace,
                      color: teamColor,
                    },
                  ]}
                />
              </ChartCard>
            </LazyChartCard>

            {/* 5. Finishing Position Distribution */}
            <LazyChartCard>
              <ChartCard
                title={m.f1_profile_chart_result_distribution()}
                tooltip={m.f1_profile_tooltip_result_distribution()}
              >
                <BarChart
                  categories={[
                    m.f1_profile_cat_win(),
                    m.f1_profile_cat_podium(),
                    m.f1_profile_cat_top10(),
                    m.f1_profile_cat_out_of_points(),
                    m.f1_profile_cat_dnf(),
                  ]}
                  series={[
                    {
                      name: "Races",
                      data: [
                        chartData.positionDistribution.wins,
                        chartData.positionDistribution.podiumsExcl1,
                        chartData.positionDistribution.top10ExclPodium,
                        chartData.positionDistribution.outOfPoints,
                        chartData.positionDistribution.dnfs,
                      ],
                      color: teamColor,
                    },
                  ]}
                  horizontal
                />
              </ChartCard>
            </LazyChartCard>

            {/* 6. Consistency Heatmap (race positions) */}
            <LazyChartCard>
              <ChartCard
                title={m.f1_profile_chart_finish_heatmap()}
                tooltip={m.f1_profile_tooltip_finish_heatmap()}
              >
                <HeatmapStrip
                  labels={chartData.labels}
                  values={chartData.raceHeatmapValues}
                  // Race cells encode inverted finishing position (P1=20…P20=1,
                  // DNF=0) — show the real result, not "pts".
                  valueFormatter={(v) => (v === 0 ? "DNF" : `P${21 - v}`)}
                />
              </ChartCard>
            </LazyChartCard>

            {/* 7. Race Performance Breakdown */}
            <LazyChartCard>
              <ChartCard
                title={m.f1_profile_chart_race_perf_breakdown()}
                tooltip={m.f1_profile_tooltip_race_perf()}
              >
                <BarChart
                  categories={[
                    m.f1_profile_cat_fastest_laps(),
                    m.f1_profile_cat_dotd(),
                    m.f1_profile_cat_overtakes(),
                    m.f1_profile_cat_positions_gained(),
                  ]}
                  series={[
                    {
                      name: "Count",
                      data: [
                        chartData.raceBreakdown.fastestLaps,
                        chartData.raceBreakdown.dotd,
                        chartData.raceBreakdown.totalOvertakes,
                        chartData.raceBreakdown.totalPosGained,
                      ],
                      color: teamColor,
                    },
                  ]}
                  horizontal
                />
              </ChartCard>
            </LazyChartCard>

            {/* 8. Avg Finishing Position by Track Type */}
            <LazyChartCard>
              <ChartCard
                title={m.f1_profile_chart_avg_pos_track_type()}
                tooltip={m.f1_profile_tooltip_avg_pos_track()}
              >
                <BarChart
                  categories={chartData.trackRacePos
                    .filter((t) => t.count > 0)
                    .map((t) =>
                      t.type
                        .replace("_", " ")
                        .replace(/\b\w/g, (c) => c.toUpperCase()),
                    )}
                  series={[
                    {
                      name: "Avg Position",
                      data: chartData.trackRacePos
                        .filter((t) => t.count > 0)
                        .map((t) => t.avgPos ?? 0),
                      color: teamColor,
                    },
                  ]}
                  horizontal
                />
              </ChartCard>
            </LazyChartCard>

            {/* 9. Head-to-Head Race Positions vs Teammate */}
            {chartData.h2hRaceData && (
              <LazyChartCard>
                <ChartCard
                  title={m.f1_profile_vs_teammate({
                    name: profile.teammateName ?? m.f1_profile_teammate(),
                  })}
                  tooltip={m.f1_profile_tooltip_h2h_race()}
                >
                  <H2HPositionChart
                    labels={chartData.h2hRaceData.h2hLabels}
                    driverName={profile.driverFullName}
                    teammateName={
                      profile.teammateName ?? m.f1_profile_teammate()
                    }
                    driverPositions={chartData.h2hRaceData.driverPos}
                    teammatePositions={chartData.h2hRaceData.teammatePos}
                    driverColor={teamColor}
                  />
                </ChartCard>
              </LazyChartCard>
            )}

            {/* 10. Head-to-Head Qualifying Positions vs Teammate */}
            {chartData.h2hQualiData && (
              <LazyChartCard>
                <ChartCard
                  title={m.f1_profile_chart_h2h_qualifying({
                    name: profile.teammateName ?? m.f1_profile_teammate(),
                  })}
                  tooltip={m.f1_profile_tooltip_h2h_quali()}
                >
                  <H2HPositionChart
                    labels={chartData.h2hQualiData.h2hLabels}
                    driverName={profile.driverFullName}
                    teammateName={
                      profile.teammateName ?? m.f1_profile_teammate()
                    }
                    driverPositions={chartData.h2hQualiData.driverPos}
                    teammatePositions={chartData.h2hQualiData.teammatePos}
                    driverColor={teamColor}
                  />
                </ChartCard>
              </LazyChartCard>
            )}
          </div>
        ) : (
          chartData && (
            <div className="grid gap-4 lg:grid-cols-2">
              {/* 1. Points Over Time */}
              <ChartCard
                title={m.f1_profile_chart_points_over_time()}
                tooltip={m.f1_profile_tooltip_fantasy_pts_per_race()}
              >
                <LineChart
                  categories={chartData.labels}
                  series={[
                    {
                      name: "Points",
                      data: chartData.points,
                      color: teamColor,
                      areaStyle: true,
                    },
                  ]}
                  showLegend={false}
                />
              </ChartCard>

              {/* 2. Cumulative Fantasy Points */}
              <ChartCard
                title={m.f1_profile_chart_cumulative_fantasy()}
                tooltip={m.f1_profile_tooltip_cumulative_fantasy()}
              >
                <LineChart
                  categories={chartData.labels}
                  series={[
                    {
                      name: "Total Points",
                      data: chartData.cumulativeFantasyPoints,
                      color: teamColor,
                      areaStyle: true,
                    },
                  ]}
                  showLegend={false}
                />
              </ChartCard>

              {/* 3. Qualifying vs Race Position (shared) */}
              <LazyChartCard>
                <ChartCard
                  title={m.f1_profile_chart_quali_vs_race()}
                  tooltip={m.f1_profile_tooltip_quali_vs_race_short()}
                >
                  <LineChart
                    categories={chartData.labels}
                    series={[
                      {
                        name: m.f1_profile_series_qualifying(),
                        data: chartData.qualiPositions,
                        color: "#8b5cf6",
                      },
                      {
                        name: m.f1_profile_series_race(),
                        data: chartData.racePositions,
                        color: teamColor,
                      },
                    ]}
                    invertYAxis
                    yAxisName={m.f1_profile_yaxis_position()}
                  />
                </ChartCard>
              </LazyChartCard>

              {/* 4. Positions Gained Per Race (shared) */}
              <LazyChartCard>
                <ChartCard
                  title={m.f1_profile_chart_pos_gained()}
                  tooltip={m.f1_profile_tooltip_pos_gained()}
                >
                  <BarChart
                    categories={chartData.labels}
                    series={[
                      {
                        name: "Positions",
                        data: chartData.positionsGainedPerRace,
                        color: teamColor,
                      },
                    ]}
                  />
                </ChartCard>
              </LazyChartCard>

              {/* 5. Consistency Heatmap */}
              <LazyChartCard>
                <ChartCard
                  title={m.f1_profile_chart_consistency()}
                  tooltip={m.f1_profile_tooltip_consistency_heatmap()}
                >
                  <HeatmapStrip
                    labels={chartData.labels}
                    values={chartData.heatmapValues}
                  />
                </ChartCard>
              </LazyChartCard>

              {/* 6. Points Breakdown */}
              <LazyChartCard>
                <ChartCard
                  title={m.f1_profile_chart_points_breakdown()}
                  tooltip={m.f1_profile_tooltip_points_breakdown()}
                >
                  <BarChart
                    categories={[
                      m.f1_profile_cat_top10_quali(),
                      m.f1_profile_cat_overtakes(),
                      m.f1_profile_cat_pos_gained_short(),
                      m.f1_profile_cat_fastest_lap(),
                      m.f1_profile_cat_dotd_short(),
                    ]}
                    series={[
                      {
                        name: "Count",
                        data: [
                          chartData.radarData.qualiPtsProxy,
                          chartData.radarData.overtakePts,
                          chartData.radarData.posGainPts,
                          chartData.radarData.flPts,
                          chartData.radarData.dotdPts,
                        ],
                        color: teamColor,
                      },
                    ]}
                    horizontal
                  />
                </ChartCard>
              </LazyChartCard>

              {/* 7. Fantasy Score Distribution */}
              <LazyChartCard>
                <ChartCard
                  title={m.f1_profile_chart_fantasy_score_dist()}
                  tooltip={m.f1_profile_tooltip_fantasy_score_dist()}
                >
                  <BarChart
                    categories={[
                      "≤0 pts",
                      "1–14 pts",
                      "15–29 pts",
                      "30–44 pts",
                      "45+ pts",
                    ]}
                    series={[
                      {
                        name: "Races",
                        data: chartData.fantasyScoreDistribution,
                        color: teamColor,
                      },
                    ]}
                    horizontal
                  />
                </ChartCard>
              </LazyChartCard>

              {/* 8. Track-Type Performance (shared) */}
              <LazyChartCard>
                <ChartCard
                  title={m.f1_profile_chart_track_type()}
                  tooltip={m.f1_profile_tooltip_track_type_constructor()}
                >
                  <BarChart
                    categories={chartData.trackPerf.map((t) =>
                      t.type
                        .replace("_", " ")
                        .replace(/\b\w/g, (c) => c.toUpperCase()),
                    )}
                    series={[
                      {
                        name: "Avg Points",
                        data: chartData.trackPerf.map((t) => t.avgPts),
                        color: teamColor,
                      },
                    ]}
                  />
                  <div className="mt-3 space-y-1">
                    {chartData.trackPerf
                      .filter((t) => t.count > 0)
                      .map((t) => (
                        <div
                          key={t.type}
                          className="flex gap-1.5 text-[11px] text-muted-foreground"
                        >
                          <span className="font-medium capitalize shrink-0">
                            {t.type.replace("_", " ")}:
                          </span>
                          <span>
                            {(chartData.trackNames[t.type] ?? []).join(", ")}
                          </span>
                        </div>
                      ))}
                  </div>
                </ChartCard>
              </LazyChartCard>

              {/* 9. Head-to-Head Fantasy Points vs Teammate (shared slot) */}
              {chartData.h2hData && (
                <LazyChartCard>
                  <ChartCard
                    title={m.f1_profile_vs_teammate({
                      name: profile.teammateName ?? m.f1_profile_teammate(),
                    })}
                    tooltip={m.f1_profile_tooltip_fantasy_h2h()}
                  >
                    <H2HPositionChart
                      labels={chartData.h2hData.h2hLabels}
                      driverName={profile.driverFullName}
                      teammateName={
                        profile.teammateName ?? m.f1_profile_teammate()
                      }
                      driverPositions={chartData.h2hData.driverPts}
                      teammatePositions={chartData.h2hData.teammatePts}
                      driverColor={teamColor}
                      lowerIsBetter={false}
                      valuePrefix=""
                      unitLabel={m.f1_profile_unit_pts()}
                    />
                  </ChartCard>
                </LazyChartCard>
              )}

              {/* 10. Head-to-Head Qualifying Positions (shared slot) */}
              {chartData.h2hQualiData && (
                <LazyChartCard>
                  <ChartCard
                    title={m.f1_profile_chart_h2h_qualifying({
                      name: profile.teammateName ?? m.f1_profile_teammate(),
                    })}
                    tooltip={m.f1_profile_tooltip_h2h_quali()}
                  >
                    <H2HPositionChart
                      labels={chartData.h2hQualiData.h2hLabels}
                      driverName={profile.driverFullName}
                      teammateName={
                        profile.teammateName ?? m.f1_profile_teammate()
                      }
                      driverPositions={chartData.h2hQualiData.driverPos}
                      teammatePositions={chartData.h2hQualiData.teammatePos}
                      driverColor={teamColor}
                    />
                  </ChartCard>
                </LazyChartCard>
              )}

              {/* 11. Price History (fantasy only) */}
              <LazyChartCard>
                <ChartCard
                  title={m.f1_profile_chart_price_history()}
                  tooltip={m.f1_profile_tooltip_constructor_price()}
                >
                  <LineChart
                    categories={chartData.labels}
                    series={[
                      {
                        name: "Price",
                        data: chartData.prices,
                        color: "#eab308",
                        areaStyle: true,
                      },
                    ]}
                    yAxisName={m.f1_profile_unit_dollar_m()}
                    showLegend={false}
                  />
                </ChartCard>
              </LazyChartCard>
            </div>
          )
        )}
      </div>
    </div>
  );
}
